import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Color, ISelectOption, LifecycleAware, Masks } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { formatHbpsInput, handleBpsInput } from '../buyer-configuration-helpers';

export interface AdminManageBuyerPayLaterChanges {
    label: string;
    oldData: string | number;
    newData: string | number;
}

@LifecycleAware()
@Component({
    selector: 'admin-buyer-configuration-pay-later',
    template: `
        <buyer-configuration-section
            [hasClickedSave]="hasClickedSave"
            sectionFormControlName="payLaterEnabled"
            [sectionFormGroup]="formGroup"
            sectionTitle="FlexTerm"
        >
            <form [style.padding]="'2rem 3rem'" [formGroup]="formGroup">
                <p class="font-weight-400 text-black">
                    LSQ will make all payments to enrolled suppliers for this buyer. When this
                    feature is enabled, it triggers a term payment file to be generated with payment
                    instructions for invoices that have reached terms while simultaneously creating
                    a receivable for ledgering. Payment of invoices by LSQ in this program do not
                    constitute the closure of the invoices as they are still owed to LSQ by the
                    buyer.
                </p>
                <p class="font-weight-400 text-black pt-3">
                    Please note that Total Payment Managment must be enabled to use FlexTerm.
                </p>
                <br />

                <div class="row align-items-end">
                    <div class="col-6">
                        <lsq-label
                            label="FlexTerm Credit Limit*"
                            subLabel="Buyer's credit limit for FlexTerm, input as dollars with cents."
                        ></lsq-label>
                        <lsq-currency-input
                            [basicLabel]="true"
                            name="facilityLimit"
                            formControlName="facilityLimit"
                            [group]="formGroup"
                        >
                        </lsq-currency-input>
                    </div>

                    <div class="col-6">
                        <lsq-label
                            label="FlexTerm Credit Limit Currency*"
                            subLabel="The currency the credit limit is set in."
                        ></lsq-label>
                        <lsq-select
                            [basicLabel]="true"
                            name="facilityLimitCurrencyCode"
                            formControlName="facilityLimitCurrencyCode"
                            [group]="formGroup"
                            [options]="currencyOptions"
                        >
                        </lsq-select>
                    </div>
                </div>

                <div class="divider"></div>

                <div class="row align-items-end" [style.marginBottom]="'1rem'">
                    <div class="col-6">
                        <lsq-label
                            label="FlexTerm Days Extension*"
                            subLabel="The number of days after invoice terms the buyer is paying for additional extension by financing with LSQ.
                                    The number of days assigned with FlexTerm will begin on the next day after invoice terms and will begin accruing
                                    late fees for the financing costs. A value between 0 and 60 can be entered in the text box below."
                        ></lsq-label>
                        <lsq-input
                            [basicLabel]="true"
                            name="daysIncrement"
                            formControlName="daysIncrement"
                            [group]="formGroup"
                            [appendIcon]="[icons.FaHashtag, color.GraphicGray]"
                            type="number"
                            [digitsOnly]="true"
                            [min]="0"
                            [max]="60"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-6">
                        <lsq-label
                            [style.marginBottom]="'6.6rem'"
                            label="FlexTerm Max Extended Terms"
                            subLabel="The maximum number of days past the invoice date that a payment extension can be applied"
                        ></lsq-label>

                        <lsq-input
                            [basicLabel]="true"
                            name="maxExtendableInvoiceAgeDays"
                            formControlName="maxExtendableInvoiceAgeDays"
                            [group]="formGroup"
                            [appendIcon]="[icons.FaHashtag, color.GraphicGray]"
                            type="number"
                            [digitsOnly]="true"
                            [min]="0"
                            [max]="150"
                        >
                        </lsq-input>
                    </div>
                </div>

                <div class="row align-items-end">
                    <div class="col-6">
                        <lsq-label
                            label="FlexTerm Primary Fee Rate*"
                            subLabel="The daily rate a buyer pays in fees to cover the financing cost of the FlexTerm invoices. Set to whole percentage up to four decimal places."
                        ></lsq-label>
                        <lsq-input
                            [basicLabel]="true"
                            name="feeDailyPercent"
                            formControlName="feeDailyPercent"
                            [group]="formGroup"
                            [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                            [textFormatter]="formatHbpsInput"
                            [handleKeypress]="handleBpsInput"
                            [mask]="hbpsAsPercentMask"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-6">
                        <lsq-label
                            label="FlexTerm Secondary Fee Rate*"
                            subLabel="The daily rate for a buyer’s invoices if the buyer does not remit LSQ for FlexTerm invoices before the FlexTerm due date."
                        ></lsq-label>
                        <lsq-input
                            [basicLabel]="true"
                            name="lateFeeDailyPercent"
                            formControlName="lateFeeDailyPercent"
                            [group]="formGroup"
                            [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                            [textFormatter]="formatHbpsInput"
                            [handleKeypress]="handleBpsInput"
                            [mask]="hbpsAsPercentMask"
                        >
                        </lsq-input>
                    </div>
                </div>
            </form>
        </buyer-configuration-section>
    `
})
export class AdminBuyerConfigurationPayLaterComponent {
    @Input() public formGroup: FormGroup;
    @Input() public hasClickedSave: boolean;
    public hbpsAsPercentMask = Masks.HbpsAsPercentMask;
    public formatHbpsInput = formatHbpsInput;
    public handleBpsInput = handleBpsInput;

    public currencyOptions: ISelectOption[] = [{ label: 'USD', value: 'USD' }];
    public icons = Icons;
    public color = Color;
}
